import { MbeAlert, MbePasswordOff, MbePasswordOn, MbeUpload } from '@/components/icons';
import type { InputProps } from '@chakra-ui/react';
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useBoolean,
  visuallyHiddenStyle,
} from '@chakra-ui/react';
import React, { forwardRef } from 'react';
import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { IFile } from '@/types';

interface IErrorMessage {
  message: string;
}

export const ErrorMessage = ({ message }: IErrorMessage): JSX.Element => {
  return (
    <FormErrorMessage left={0} position="absolute" top="100%">
      <MbeAlert h={5} mr={2} w={5} /> {message}
    </FormErrorMessage>
  );
};

interface IInputProps extends InputProps {
  error?: FieldError;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
}

export const MbeInput = forwardRef<
  HTMLInputElement,
  IInputProps
  // eslint-disable-next-line @typescript-eslint/naming-convention
>(function MbeInput(props, ref) {
  const { error, id, leftElement, name, placeholder, rightElement, ...rest } = props;
  return (
    <FormControl isInvalid={!!error} variant="floating">
      <InputGroup>
        {leftElement}
        <Input id={id ?? name} name={name} placeholder={placeholder} ref={ref} {...rest} />
        <FormLabel htmlFor={id ?? name}>{placeholder}</FormLabel>
        {rightElement}
      </InputGroup>
      {error?.message && <ErrorMessage message={error.message} />}
    </FormControl>
  );
});

export const PasswordInput = forwardRef<
  HTMLInputElement,
  IInputProps
  // eslint-disable-next-line @typescript-eslint/naming-convention
>(function PasswordInput(props, ref) {
  const [isPasswordVisible, setShowPassword] = useBoolean(false);
  const { error, placeholder, ...rest } = props;
  return (
    <FormControl isInvalid={!!error} variant="floating">
      <InputGroup>
        <Input
          placeholder={placeholder}
          ref={ref}
          type={isPasswordVisible ? 'text' : 'password'}
          {...rest}
        />
        <FormLabel htmlFor="password">{placeholder}</FormLabel>
        <InputRightElement>
          <IconButton
            aria-label="Toogle password visibility"
            variant="unstyled"
            onClick={setShowPassword.toggle}
          >
            {isPasswordVisible ? (
              <MbePasswordOn h="38px" w="38px" />
            ) : (
              <MbePasswordOff h="38px" w="38px" />
            )}
          </IconButton>
        </InputRightElement>
      </InputGroup>
    </FormControl>
  );
});

interface IFileUploadInput extends InputProps {
  error?: Merge<FieldError, (Merge<FieldError, FieldErrorsImpl<IFile>> | undefined)[]>;
}

export const FileUploadInput = forwardRef<HTMLInputElement, IFileUploadInput>(
  // eslint-disable-next-line @typescript-eslint/naming-convention
  function FileUploadInput(props, ref) {
    const { error, ...rest } = props;
    const { t } = useTranslation();
    return (
      <FormControl isInvalid={!!error}>
        <Input
          left={0}
          opacity={0}
          position="absolute"
          ref={ref}
          top={0}
          type="file"
          variant="unstyled"
          zIndex="-1"
          {...rest}
        />
        <FormLabel color="brand.red.light" cursor="pointer" htmlFor={props.id}>
          <MbeUpload h={8} mr={2} w={8} />
          {t('FORM.UPLOAD_BTN_LABEL')}
        </FormLabel>
        <Text as="span" fontSize="small" lineHeight="none">
          {t('FORM.UPLOAD_LABEL')}
        </Text>
        {error?.message && <ErrorMessage message={error.message} />}
      </FormControl>
    );
  }
);

export const CheckboxCard = forwardRef<
  HTMLInputElement,
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
    children: React.ReactNode;
  }
>(
  // eslint-disable-next-line @typescript-eslint/naming-convention
  function CheckboxCard(props, ref) {
    const { checked: isChecked, children, ...rest } = props;
    return (
      <Box
        _checked={{
          borderTopColor: 'brand.red.light',
          borderTopWidth: '2px',
        }}
        _hover={{
          boxShadow: 'xl',
        }}
        alignItems="center"
        as="label"
        borderTopColor="white"
        borderTopWidth="2px"
        boxShadow="md"
        cursor="pointer"
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        data-checked={isChecked || undefined}
        display="flex"
        flexBasis={{ base: 'calc((100% / 2) - 10px)', md: 'calc((100% / 3) - 20px)' }}
        fontSize="small"
        height="80px"
        justifyContent="center"
        p={2}
        textAlign="center"
      >
        <input
          checked={isChecked}
          hidden
          ref={ref}
          style={visuallyHiddenStyle}
          type="checkbox"
          {...rest}
        />
        {children}
      </Box>
    );
  }
);
